import React, { useEffect, useRef, useState } from "react";

import { useAppSelector } from "../../app/hooks";
import { Controller, useFormContext } from "react-hook-form";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { OptionsProps, OrderItemProcess } from "../../types/order";
import { getItems } from "../../features/newOrder/newOrderSlice";

interface ProcessFormProps {
  index: number;
  processIndex: number;
}

const ProcessForm = ({ index, processIndex }: ProcessFormProps) => {
  const items = useAppSelector(getItems);
  const qtyInputRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const {
    register,
    control,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
    watch,
  } = useFormContext();

  const itemId = watch(`items.${index}.item_id`);

  const [processes, setProcesses] = useState<OrderItemProcess[]>([]);
  const [processOption, setProcessOption] = useState<OptionsProps[]>([]);
  const [unitOption, setUnitOption] = useState<OptionsProps[]>([]);
  const [unitType, setUnitType] = useState<number | null>(null);
  const [processError, setProcessError] = useState<boolean>(false);

  const handleChangeProcess = (process_id: string, processIndex: number) => {
    setProcessError(false);
    const items = getValues(`items`);

    // Check if this process is already selected in any other process of the same item
    const currentItemProcesses = items[index]?.processes || [];
    const isDuplicate = currentItemProcesses.some(
      (process: any, idx: number) =>
        idx !== processIndex && process.process_id === parseInt(process_id)
    );

    if (process_id && !isDuplicate) {
      const process = processes.find(
        (p) => p.process.id === parseInt(process_id)
      );

      const units =
        process?.units
          ?.filter((unit) => unit.rate > 0)
          ?.map((unit) => ({
            value: unit.unit_id,
            label: unit.unit.name,
            rate: unit.rate || 0,
          })) || [];

      setUnitOption(units);
      setValue(
        `items.${index}.processes.${processIndex}.process_id`,
        process_id
      );
      clearErrors(`items.${index}.processes.${processIndex}.process_id`);

      if (units.length === 1) {
        setValue(
          `items.${index}.processes.${processIndex}.rate`,
          units[0].rate
        );
        setValue(
          `items.${index}.processes.${processIndex}.unit_id`,
          units[0].value,
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        clearErrors(`items.${index}.processes.${processIndex}.unit_id`);
        setUnitType(units[0].value);

        setTimeout(() => {
          const qtyInputWrapper =
            qtyInputRefs.current[`${index}_${processIndex}`];
          if (qtyInputWrapper) {
            const input = qtyInputWrapper.querySelector("input");
            if (input) {
              input.focus();
            }
          }
        }, 100);
      } else {
        setValue(`items.${index}.processes.${processIndex}.rate`, null);
        setValue(`items.${index}.processes.${processIndex}.unit_id`, 0);
        setUnitType(null);
      }
    } else {
      setProcessError(true);
    }
  };

  const handleChangeUnit = (unit_id: string, processIndex: number) => {
    const process_id = getValues(
      `items.${index}.processes.${processIndex}.process_id`
    );
    const process = processes.find(
      (p) => p.process.id === parseInt(process_id)
    );

    if (process) {
      const unit = process.units.find((u) => u.unit_id === parseInt(unit_id));
      if (unit) {
        setValue(`items.${index}.processes.${processIndex}.rate`, unit.rate);
        setValue(`items.${index}.processes.${processIndex}.unit_id`, unit_id);
        clearErrors(`items.${index}.processes.${processIndex}.unit_id`);
        setUnitType(parseInt(unit_id));

        setTimeout(() => {
          const qtyInputWrapper =
            qtyInputRefs.current[`${index}_${processIndex}`];
          if (qtyInputWrapper) {
            const input = qtyInputWrapper.querySelector("input");
            if (input) {
              input.focus();
            }
          }
        }, 100);
        return;
      }
    }

    setValue(`items.${index}.processes.${processIndex}.rate`, null);
    setValue(`items.${index}.processes.${processIndex}.unit_id`, 0);
    setUnitType(null);
  };

  useEffect(() => {
    const processData =
      items.find((i) => i.item.id === itemId)?.processes || [];
    setProcesses(processData);

    setProcessOption(
      processData.map((process) => ({
        value: process.process.id,
        label: process.process.name,
      })) || []
    );

    if (processData.length === 1) {
      setTimeout(() => {
        const process_id = processData[0].process.id;

        setProcessError(false);
        const currentItems = getValues(`items`);

        // Check if this process is already selected in any other process of the same item
        const currentItemProcesses = currentItems[index]?.processes || [];
        const isDuplicate = currentItemProcesses.some(
          (process: any, idx: number) =>
            idx !== processIndex && process.process_id === process_id
        );

        if (process_id && !isDuplicate) {
          // Use processData instead of processes state since it's more current
          const process = processData.find((p) => p.process.id === process_id);

          const units =
            process?.units
              ?.filter((unit) => unit.rate > 0)
              ?.map((unit) => ({
                value: unit.unit_id,
                label: unit.unit.name,
                rate: unit.rate || 0,
              })) || [];

          setUnitOption(units);
          setValue(
            `items.${index}.processes.${processIndex}.process_id`,
            process_id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
          clearErrors(`items.${index}.processes.${processIndex}.process_id`);

          if (units.length === 1) {
            setValue(
              `items.${index}.processes.${processIndex}.rate`,
              units[0].rate
            );
            setValue(
              `items.${index}.processes.${processIndex}.unit_id`,
              units[0].value,
              {
                shouldValidate: true,
                shouldDirty: true,
              }
            );
            clearErrors(`items.${index}.processes.${processIndex}.unit_id`);
            setUnitType(units[0].value);

            setTimeout(() => {
              const qtyInputWrapper =
                qtyInputRefs.current[`${index}_${processIndex}`];
              if (qtyInputWrapper) {
                const input = qtyInputWrapper.querySelector("input");
                if (input) {
                  input.focus();
                }
              }
            }, 100);
          } else {
            setValue(`items.${index}.processes.${processIndex}.rate`, null);
            setValue(`items.${index}.processes.${processIndex}.unit_id`, 0);
            setUnitType(null);
          }
        } else {
          setProcessError(true);
        }
      }, 500);
    }
  }, [itemId, items, processIndex, index, getValues, setValue, clearErrors]);

  return (
    <div className="relative px-2 py-4 w-full mt-6 before:content[''] before:bg-gradient-to-r before:from-primary-1 before:to-primary-2 before:absolute before:top-0 before:left-0 before:w-full before:h-full before:rounded before:-z-[1]">
      <div className="">
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <Controller
            {...register(
              `items.${index}.processes.${processIndex}.process_id`,
              {
                required: true,
                min: 1,
              }
            )}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={
                  processOption.find((val) => val.value === field?.value) ||
                  null
                }
                className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                styles={selectStyles}
                placeholder={"પ્રોસેસ નું નામ સિલેક્ટ કરો"}
                options={processOption}
                onChange={(val: any) =>
                  handleChangeProcess(val?.value, processIndex)
                }
              />
            )}
          />
        </div>
        {Array.isArray(errors?.items) &&
          errors.items[index]?.processes[processIndex]?.process_id && (
            <span className="text-red-500">પ્રોસેસ નું નામ સિલેક્ટ કરો</span>
          )}

        {processError && (
          <span className="text-red-500">
            {Array.isArray(errors?.items) &&
              errors.items[index]?.processes[processIndex]?.process_id !==
                undefined &&
              ", "}
            આ પ્રોસેસ પેહલાં થી ઉમેરેલ છે.
          </span>
        )}
      </div>

      <div className="mt-6">
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <Controller
            {...register(`items.${index}.processes.${processIndex}.unit_id`, {
              required: true,
              min: 1,
            })}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={
                  unitOption.find((val) => val.value === field?.value) || null
                }
                className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                styles={selectStyles}
                placeholder={"યુનિટ નું નામ સિલેક્ટ કરો"}
                options={unitOption}
                onChange={(val: any) =>
                  handleChangeUnit(val?.value, processIndex)
                }
              />
            )}
          />
        </div>
        {Array.isArray(errors?.items) &&
          errors.items[index]?.processes[processIndex]?.unit_id && (
            <span className="text-red-500">યુનિટ નું નામ સિલેક્ટ કરો</span>
          )}
      </div>

      <div className="mt-6 hidden">
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <input
            className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
            type="number"
            step="any"
            pattern="[0-9]+(\.[0-9]+)?"
            placeholder="રેટ/ભાવ દાખલ કરો"
            {...register(`items.${index}.processes.${processIndex}.rate`, {
              required: true,
              min: 1,
            })}
            onFocus={(e) =>
              e.target.addEventListener(
                "wheel",
                function (e) {
                  e.preventDefault();
                },
                { passive: false }
              )
            }
          />
        </div>
        {Array.isArray(errors?.items) &&
          errors.items[index]?.processes[processIndex]?.rate && (
            <span className="text-red-500">રેટ/ભાવ દાખલ કરો</span>
          )}
      </div>

      <div className="mt-6">
        <div
          ref={(el: any) => {
            if (qtyInputRefs && qtyInputRefs.current) {
              qtyInputRefs.current[`${index}_${processIndex}`] = el;
            }
          }}
          className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded"
        >
          <input
            className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
            type="number"
            step="any"
            pattern="[0-9]+(\.[0-9]+)?"
            placeholder={
              unitType && unitType === 3
                ? "જોડી દાખલ કરો"
                : unitType && unitType === 2
                ? "નંગ દાખલ કરો"
                : unitType && unitType === 1
                ? "વજન દાખલ કરો"
                : "વજન/નંગ/જોડી દાખલ કરો"
            }
            {...register(`items.${index}.processes.${processIndex}.qty`, {
              required: true,
            })}
            onFocus={(e) =>
              e.target.addEventListener(
                "wheel",
                function (e) {
                  e.preventDefault();
                },
                { passive: false }
              )
            }
          />
        </div>
        {Array.isArray(errors?.items) &&
          errors.items[index]?.processes[processIndex]?.qty && (
            <span className="text-red-500">
              {unitType && unitType === 3
                ? "જોડી દાખલ કરો"
                : unitType && unitType === 2
                ? "નંગ દાખલ કરો"
                : unitType && unitType === 1
                ? "વજન દાખલ કરો"
                : "વજન/નંગ/જોડી દાખલ કરો"}
            </span>
          )}
      </div>

      {unitType && unitType > 1 && (
        <div className="mt-6">
          <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
            <input
              className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
              type="number"
              step="any"
              pattern="[0-9]+(\.[0-9]+)?"
              placeholder="વજન દાખલ કરો"
              {...register(`items.${index}.processes.${processIndex}.weight`, {
                required: true,
              })}
              onFocus={(e) =>
                e.target.addEventListener(
                  "wheel",
                  function (e) {
                    e.preventDefault();
                  },
                  { passive: false }
                )
              }
            />
          </div>
          {Array.isArray(errors?.items) &&
            errors.items[index]?.processes[processIndex]?.weight && (
              <span className="text-red-500">વજન દાખલ કરો</span>
            )}
        </div>
      )}

      <div className="mt-6">
        <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
          <input
            className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
            type="number"
            pattern="[0-9]*"
            placeholder="ડિઝાઇન દાખલ કરો"
            {...register(`items.${index}.processes.${processIndex}.design`, {
              required: true,
              min: 1,
            })}
            onFocus={(e) =>
              e.target.addEventListener(
                "wheel",
                function (e) {
                  e.preventDefault();
                },
                { passive: false }
              )
            }
          />
        </div>
        {Array.isArray(errors?.items) &&
          errors.items[index]?.processes[processIndex]?.design && (
            <span className="text-red-500">ડિઝાઇન દાખલ કરો</span>
          )}
      </div>
    </div>
  );
};
export default ProcessForm;
